import { formatDeviceMakeAndModel } from '@cdw-ae/cdw-grid-configs';
import type { CdwColDef } from '@cdw-ae/common-ui-components-search-grid';
import type { FilterBarField } from '@cdw-ae/filter-bar-interfaces';
import { MatchersService } from '@cdw-ae/filter-bar-matcher-configs';
import {
  multiContainsChipFormatter,
  multiContainsToolTipFormatter,
} from '../../../../../common/services/route-config/configs/filter-bar-config.helpers';
import type { AssetChangeRow } from './asset-change-data.service';

const textMatchers = () => [
  { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
  MatchersService.Matchers.fullMatch,
];

export function getDeviceAssetColumns(): CdwColDef[] {
  return [
    { field: 'cdwAssetId', headerName: 'CDW Asset ID', flex: 0.4 },
    { field: 'serialNumber', headerName: 'Serial Number', flex: 0.4 },
    { field: 'vendorId', headerName: 'Device ID (Vendor)', flex: 0.4 },
    {
      field: 'model',
      headerName: 'Model',
      flex: 0.4,
      valueGetter: (params) => {
        const row = params.data as AssetChangeRow | undefined;
        return formatDeviceMakeAndModel(row?.make, row?.model);
      },
    },
  ];
}

export function getDeviceAssetFilters(): FilterBarField[] {
  return [
    { displayName: 'Serial Number', name: 'serial_number', fieldMatchers: textMatchers() },
    { displayName: 'Device ID (Vendor)', name: 'vendor_id', fieldMatchers: textMatchers() },
    // EDUS-3135: one Model filter over the make + model concatenation, matching
    // Device Search. `make+model` is the shared filter engine's composite field.
    {
      displayName: 'Model',
      name: 'make+model',
      fieldMatchers: [
        {
          ...MatchersService.Matchers.multiContains,
          suggestMatcher: true,
          options: {
            chipFormatter: multiContainsChipFormatter,
            toolTipFormatter: multiContainsToolTipFormatter,
          },
        },
      ],
    },
  ];
}

export function getUserAssetColumns(): CdwColDef[] {
  return [
    { field: 'cdwAssetId', headerName: 'CDW Asset ID', flex: 0.4 },
    { field: 'email', headerName: 'Email', flex: 0.5 },
    { field: 'fullName', headerName: 'Full Name', flex: 0.4 },
    { field: 'orgUnitPath', headerName: 'OU Path', flex: 0.4 },
    {
      field: 'isAdmin',
      headerName: 'Admin',
      flex: 0.2,
      valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
    },
    {
      field: 'isSuspended',
      headerName: 'Suspended',
      flex: 0.2,
      valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
    },
  ];
}

export function getUserAssetFilters(): FilterBarField[] {
  return [
    { displayName: 'Email', name: 'email', fieldMatchers: textMatchers() },
    { displayName: 'Full Name', name: 'full_name', fieldMatchers: textMatchers() },
    { displayName: 'OU Path', name: 'org_unit_path', fieldMatchers: textMatchers() },
  ];
}

export function getGroupingAssetColumns(): CdwColDef[] {
  return [
    { field: 'cdwAssetId', headerName: 'CDW Asset ID', flex: 0.4 },
    { field: 'value', headerName: 'Value', flex: 0.6 },
    { field: 'vendorId', headerName: 'Vendor ID', flex: 0.4 },
  ];
}

export function getGroupingAssetFilters(): FilterBarField[] {
  return [
    { displayName: 'Value', name: 'value', fieldMatchers: textMatchers() },
    { displayName: 'Vendor ID', name: 'vendor_id', fieldMatchers: textMatchers() },
  ];
}

export function getColumnsForAssetType(assetTypeId: number): CdwColDef[] {
  // The SSRM grid defaults every column to sortable, but the asset-changes
  // search has no sort support: a header click would show an arrow and
  // reorder nothing.
  return columnsFor(assetTypeId).map((col) => ({ ...col, sortable: false }));
}

function columnsFor(assetTypeId: number): CdwColDef[] {
  switch (assetTypeId) {
    case 1: return getDeviceAssetColumns();
    case 2: return getUserAssetColumns();
    case 3: return getGroupingAssetColumns();
    default: return getDeviceAssetColumns();
  }
}

export function getFiltersForAssetType(assetTypeId: number): FilterBarField[] {
  switch (assetTypeId) {
    case 1: return getDeviceAssetFilters();
    case 2: return getUserAssetFilters();
    case 3: return getGroupingAssetFilters();
    default: return getDeviceAssetFilters();
  }
}

export function getItemLabelForAssetType(assetTypeId: number): string {
  switch (assetTypeId) {
    case 1: return 'device';
    case 2: return 'user';
    case 3: return 'grouping';
    default: return 'asset';
  }
}
